<script setup lang="ts">
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { useField } from 'vee-validate'
  import { ExclamationCircleIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/vue/24/outline'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      selectOption: 'Select options...',
      noResults: 'No results found'
    },
    de: {
      selectOption: 'Optionen auswählen...',
      noResults: 'Keine Ergebnisse gefunden'
    }
  })

  export interface Option {
    value: string | number
    title: string
    icon?: string
    color?: string
  }

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: Array as () => (string | number)[],
      required: false,
      default: () => []
    },
    rules: {
      type: [Object, String],
      required: false
    },
    options: {
      type: Array as () => Option[],
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    showAvatars: {
      type: Boolean,
      required: false,
      default: false
    },
    inputAction: {
      type: Function,
      required: false,
      default: (_newVal: string, _oldVal: string) => {},
      validator: (fn) => {
        return typeof fn === 'function' && fn.length === 2
      }
    },
    variant: {
      type: String,
      required: false,
      default: 'default',
      validator: (value: string) => ['default', 'inline-text'].includes(value)
    },
    isFilterable: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const {
    value: model,
    errorMessage,
    handleBlur,
    meta
  } = useField(props.name, props.rules, {
    initialValue: props.modelValue
  })

  watch(model, (newValue) => {
    emit('update:modelValue', newValue)
  })

  const selectedOptions = computed(() =>
    props.options.filter((option) => model.value.includes(option.value))
  )

  const containerRef = ref<HTMLElement | null>(null)
  const opened = ref(false)
  const filterQuery = ref('')
  const inputRef = ref(null)

  const filteredOptions = computed(() => {
    if (!props.isFilterable) {
      return props.options
    }
    return props.options.filter((option) =>
      option.title.toLowerCase().includes(filterQuery.value.toLowerCase())
    )
  })

  const containerVariantClasses = computed(() => {
    if (props.variant === 'inline-text') {
      return {
        base: 'border-transparent bg-transparent hover:bg-gray-50 focus-within:ring-1 focus-within:ring-purple-500 focus-within:ring-offset-0 px-0 py-0 min-h-0 inline-flex pr-4',
        error: '!border-transparent !ring-purple-500 !bg-red-50',
        disabled: 'cursor-not-allowed bg-transparent hover:bg-transparent'
      }
    }

    // Default variant
    return {
      base: 'border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 min-h-[40px] w-full px-2 py-1',
      error: 'border-red-500 focus:border-red-500 focus:ring-red-500',
      disabled: 'disabled:opacity-50'
    }
  })

  const selectedOptionVariantClasses = computed(() => {
    if (props.variant === 'inline-text') {
      return 'mx-0.5 my-0 px-1.5 py-0.5 bg-white border-gray-200 h-6 text-xs'
    }

    // Default variant
    return 'm-0 px-2 py-0.5 bg-white border-gray-200 h-7 text-xs'
  })

  const selectedOptionBaseClasses = computed(() => {
    return 'inline-flex items-center gap-1 rounded-full border border-gray-300 font-medium text-black'
  })

  const avatarClasses = computed(() => {
    if (props.variant === 'inline-text') {
      return 'flex h-4 w-4 items-center justify-center rounded-full text-xs text-white'
    }
    return 'flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white'
  })

  const avatarImageClasses = computed(() => {
    if (props.variant === 'inline-text') {
      return 'h-4 w-4 rounded-full'
    }
    return 'h-5 w-5 rounded-full'
  })

  const removeButtonClasses =
    'ml-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 focus:bg-gray-500 focus:text-white focus:outline-none'

  const removeIconClasses = 'h-1.5 w-1.5'

  const toggleOption = (option: Option) => {
    const newValue = [...model.value]
    const index = newValue.indexOf(option.value)
    if (index === -1) {
      newValue.push(option.value)
    } else {
      newValue.splice(index, 1)
    }
    model.value = newValue
    filterQuery.value = ''
  }

  const removeOption = (option: Option) => {
    model.value = model.value.filter((value) => value !== option.value)
  }

  const isSelected = (option: Option) => {
    return model.value.includes(option.value)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
      opened.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  watch(filterQuery, (newVal, oldVal) => {
    if (props.isFilterable) {
      props.inputAction(newVal, oldVal)
    }
  })

  const getInitial = (title: string): string => {
    return title.charAt(0).toUpperCase()
  }

  const getAvatarColor = (option: Option): string => {
    if (option.color) return option.color

    // Generate a consistent color based on the title
    const hash = option.title.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)

    const hue = Math.abs(hash % 360)
    return `hsl(${hue}, 65%, 55%)`
  }
</script>

<template>
  <div
    ref="containerRef"
    class="relative"
  >
    <label
      v-if="label"
      :for="name"
      class="mb-1 block text-sm font-medium text-gray-700"
    >
      {{ label }}
    </label>
    <div class="relative">
      <div
        :class="[
          'relative flex cursor-pointer items-center rounded-lg border text-start text-sm',
          containerVariantClasses.base,
          errorMessage ? containerVariantClasses.error : '',
          disabled ? containerVariantClasses.disabled : ''
        ]"
        @click="opened = true"
      >
        <div class="flex min-w-0 flex-1 flex-wrap gap-1">
          <div
            v-for="option in selectedOptions"
            :key="option.value"
            :class="[selectedOptionBaseClasses, selectedOptionVariantClasses]"
          >
            <div
              v-if="props.showAvatars && !option.icon"
              :class="avatarClasses"
              :style="{ backgroundColor: getAvatarColor(option) }"
            >
              {{ getInitial(option.title) }}
            </div>
            <img
              v-if="option.icon"
              :src="option.icon"
              :alt="option.title"
              :class="avatarImageClasses"
            />
            <span class="max-w-[80px] truncate">{{ option.title }}</span>
            <button
              type="button"
              :class="removeButtonClasses"
              @click.stop="removeOption(option)"
            >
              <span class="sr-only">Remove option</span>
              <svg
                :class="removeIconClasses"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 8 8"
              >
                <path
                  stroke-linecap="round"
                  stroke-width="1.5"
                  d="M1 1l6 6m0-6L1 7"
                />
              </svg>
            </button>
          </div>
        </div>
        <input
          v-if="isFilterable"
          v-model="filterQuery"
          :class="[
            'border-none bg-transparent p-0 focus:outline-none focus:ring-0',
            variant === 'inline-text'
              ? selectedOptions.length === 0
                ? 'min-w-32'
                : 'w-0 min-w-0'
              : 'flex-grow'
          ]"
          type="text"
          :placeholder="selectedOptions.length === 0 ? t('selectOption') : ''"
          :disabled="disabled"
          @click.stop="opened = true"
        />
        <div
          v-if="!isFilterable"
          :class="[
            'flex-grow cursor-pointer',
            variant === 'inline-text' && selectedOptions.length === 0 ? 'min-w-32' : ''
          ]"
          @click.stop="opened = true"
        >
          <span
            v-if="selectedOptions.length === 0"
            class="text-gray-500"
          >
            {{ t('selectOption') }}
          </span>
        </div>
      </div>
      <div
        v-if="errorMessage"
        :class="[
          'absolute top-1/2 -translate-y-1/2',
          variant === 'inline-text' ? 'right-3' : 'end-8'
        ]"
      >
        <ExclamationCircleIcon
          :class="['flex-shrink-0 text-red-500', variant === 'inline-text' ? 'size-4' : 'size-5']"
        />
      </div>
      <ChevronUpDownIcon
        :class="[
          'absolute top-1/2 -translate-y-1/2 text-gray-600',
          variant === 'inline-text' ? 'right-0 h-3 w-3 opacity-40' : 'end-3 h-4 w-4'
        ]"
      />

      <div
        v-show="opened"
        class="absolute z-20 mt-2 max-h-72 w-full space-y-0.5 overflow-hidden overflow-y-auto rounded-lg border
          border-gray-200 bg-white px-1 py-1"
      >
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="relative flex h-10 cursor-pointer select-none items-center py-2 pl-3 pr-9 hover:bg-gray-100"
          :class="{
            'bg-primary-100': isSelected(option)
          }"
          @click="toggleOption(option)"
        >
          <div
            v-if="props.showAvatars && !option.icon"
            class="mr-2 flex h-6 w-6 items-center justify-center rounded-full text-sm text-white"
            :style="{ backgroundColor: getAvatarColor(option) }"
          >
            {{ getInitial(option.title) }}
          </div>
          <img
            v-if="option.icon"
            :src="option.icon"
            :alt="option.title"
            class="mr-2 h-6 w-6 rounded-full"
          />
          {{ option.title }}
          <span
            v-if="isSelected(option)"
            class="text-primary-600 absolute inset-y-0 right-0 flex items-center pr-4"
          >
            <CheckIcon
              class="h-5 w-5"
              aria-hidden="true"
            />
          </span>
        </div>
        <div
          v-if="!filteredOptions.length"
          class="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500"
        >
          {{ t('noResults') }}
        </div>
      </div>
    </div>
    <div
      v-if="errorMessage"
      class="mt-1"
    >
      <p class="text-sm text-red-600">{{ errorMessage }}</p>
    </div>
  </div>
</template>
